import * as React from "react";

export type AgentFormat = "standard" | "api" | "plugin" | "widget";

interface AgentFormatSectionProps {
  value: AgentFormat;
  onChange: (value: AgentFormat) => void;
}

export function AgentFormatSection({ value, onChange }: AgentFormatSectionProps) {
  const formats: Array<{ value: AgentFormat; label: string; description: string; icon: string }> = [
    {
      value: "standard",
      label: "Standard",
      description: "Regular AI agent with chat interface",
      icon: "bot",
    },
    {
      value: "api",
      label: "API",
      description: "Agent accessible via API endpoints",
      icon: "code",
    },
    {
      value: "plugin",
      label: "Plugin",
      description: "Plugin for existing platforms",
      icon: "puzzle",
    },
    {
      value: "widget",
      label: "Widget",
      description: "Embeddable widget component",
      icon: "window",
    }
  ];

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "bot":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case "code":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        );
      case "puzzle":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
          </svg>
        );
      case "window":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Agent Format</h3>
      </div>
      <div className="p-6">
        <div className="space-y-3">
          {formats.map((format) => (
            <label key={format.value} className="flex items-center cursor-pointer group">
              <input
                type="radio"
                name="agent-format"
                value={format.value}
                checked={value === format.value}
                onChange={(e) => onChange(e.target.value as AgentFormat)}
                className="w-4 h-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center">
                  <span className="mr-2 text-gray-500 group-hover:text-primary">
                    {getIcon(format.icon)}
                  </span>
                  <span className="text-sm font-medium text-gray-700">{format.label}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{format.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}